import React from 'react';
import { useTranslate } from 'nucleus-text';
import useTimezone from '@hooks/useTimezone';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import { CSSObject } from '@emotion/react';
import Icon from '@components/shared/Icon';

interface DateTimeRange {
  start: string;
  end: string;
}

const useStyles = (): Record<string, CSSObject> => {
  const theme = useTheme();
  const { isMobile, mediaIsXL } = useBreakpoints();

  return {
    flexContainerOuter: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      marginBottom: 0,
      fontSize: isMobile ? 16 : 20,
      [mediaIsXL]: {
        alignItems: 'center',
        flexDirection: 'row',
        marginBottom: 4
      }
    },
    dateText: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8,
      [mediaIsXL]: {
        marginBottom: 0
      }
    },
    timeText: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8,
      [mediaIsXL]: {
        marginBottom: 0
      }
    },
    divider: {
      marginLeft: theme.spacing['6'],
      marginRight: theme.spacing['6'],
      display: 'none',
      [mediaIsXL]: {
        display: 'inline-block'
      }
    },
    spacing: {
      marginLeft: theme.spacing['3']
    }
  };
};

function DateTimeRange({ startDate, endDate }: { startDate: Date; endDate: Date }): JSX.Element {
  const { flexContainerOuter, spacing, dateText, timeText, divider } = useStyles();
  const { date } = useTimezone();
  const { translate } = useTranslate();
  const theme = useTheme();

  const timeRange = (): string => {
    const startingTime = date(new Date(startDate), {
      hour: 'numeric',
      minute: 'numeric'
    });
    const endingTime = date(new Date(endDate), {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short'
    });
    return translate('Universal-TimeRange', {
      startTime: startingTime,
      endTime: endingTime
    });
  };

  return (
    <div css={flexContainerOuter}>
      <div css={dateText}>
        <Icon name="calendar" size="md" aria-hidden />
        <span css={spacing}>
          {date(new Date(startDate), {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </span>
      </div>
      <span css={divider}>|</span>
      <div css={timeText}>
        <Icon name="clock" size="md" color={theme?.font?.color?.base} aria-hidden />
        <span css={spacing}>{timeRange()}</span>
      </div>
    </div>
  );
}

export default DateTimeRange;
